import isEmpty from 'lodash/isEmpty';
import { NotificationManager } from 'react-notifications';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { axiosinstance, Sources } from 'shared/utils/api';
import { queryClient } from 'shared/utils/queryClient';
import { useEntityDisplayName } from '../misc/fields';
import { useRequestParams } from './misc';

const entityName = QueryKeys.SOURCE;
const baseUrl = `/v1/${entityName}`;

export function useSourceList(reqParams = {}, queryOptions = {}) {
  const params = useRequestParams(reqParams);
  const queryKey = [entityName, 'list', params];
  const { data, ...queryResult } = useInfiniteQuery(
    queryKey,
    async ({ pageParam: page = 1 }) => {
      const res = await axiosinstance.get(`/v1/${entityName}`, {
        params: { ...params, page },
      });
      return res.data;
    },
    {
      getNextPageParam: ({ page_number: current, total_pages: total }) => {
        const nextPage = current < total ? current + 1 : null;
        return nextPage ?? undefined;
      },
      ...queryOptions,
    }
  );

  function combinePagesData(data) {
    const initialValues = [];
    return !isEmpty(data) && data.pages && !isEmpty(data.pages)
      ? data.pages.reduce((acc, page) => [...acc, ...page.data], initialValues)
      : initialValues;
  }

  return { ...queryResult, data: combinePagesData(data) };
}

/**
 * @return {import('react-query').UseQueryResult & {
 *    data: Record<string, any>;
 * }}
 */
export function useSource(queryOptions = {}) {
  const { id } = useParams();
  const sourceId = parseInt(id);
  const fetchSource = () =>
    axiosinstance
      .get(`${baseUrl}/${sourceId}`)
      .then(({ data: res }) => res.data);
  const { data, ...queryResult } = useQuery([entityName, sourceId], fetchSource, queryOptions);

  return {
    data: data || {},
    ...queryResult,
  };
}

export const useSourceAttachments = id => {
	const fetchAttachments = () =>
		id
			? axiosinstance
					.get(`/v1/source/attachments?ids=${id}`)
					.then(res => res.data)
			: [];
	const { data, isLoading, error, refetch, isFetching } = useQuery(
		['sourceAttachments', parseInt(id)],
		fetchAttachments
	);

	return {
		data,
		isLoading,
		error,
		refetch,
		isFetching,
	};
};

export const useSourceAttachmentUrl = assetId => {
  const fetchAttachmentUrl = () =>
    assetId
      ? axiosinstance
          .get(`/v1/asset/${assetId}/download`)
          .then(res => res.data)
      : null;
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['sourceUrlAttachment', parseInt(assetId)],
    fetchAttachmentUrl
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

export function useSourceUpsert() {
  const keysToInvalidate = [
    [entityName, 'list'],
    [entityName, 'options'],
  ];
  const [sourceSingular] = useEntityDisplayName(EntityType.SOURCE);

  const mutationFn = (source) =>
    (source.id ? Sources.update(source) : Sources.create(source)).then(
      (res) => res?.data
    );

  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn,
    onSuccess: (source, payload) => {
      const { id } = payload;
      const isUpdate = !!id;

      if (isUpdate) {
        const sourceId = parseInt(id);
        queryClient.invalidateQueries([entityName, sourceId]);
        queryClient.invalidateQueries([entityName, 'list'], {
          refetchInactive: true,
        });
      } else {
        keysToInvalidate.forEach((k) => queryClient.invalidateQueries(k));
      }

      NotificationManager.success(
        `${sourceSingular} successfully ${isUpdate ? 'updated' : 'created'}.`
      );
    },
  });

  return [mutateAsync, mutationObject];
}
